import type { CSSObject } from "@emotion/react";
import { Theme } from "@mui/material/styles";
import { withBackdropSupport } from "@themes/utils/glass/backdropSupport";
import { createGlassBorderStyles } from "@themes/utils/glass/glassBorder";
import { createGlassSurfaceStyles } from "@themes/utils/glass/glassSurface";

export const getGlassBackdropStyles = (theme: Theme): CSSObject => ({
  ...withBackdropSupport({
    blur: "2px",
    saturate: theme.vars.appEffects.glass.saturateXs
  }),
  backgroundColor: theme.vars.palette.overlay.backdrop
});

export const getGlassElementStyles = (
  theme: Theme,
  isHighlighted?: boolean,
  borderRadius?: number
): CSSObject => {
  const resolvedBorderRadius = borderRadius ?? 999;
  const isHighlightedResolved = isHighlighted ?? true;
  const elementTokens = isHighlightedResolved
    ? theme.vars.ui.glassInteractive.emphasized
    : theme.vars.ui.glassInteractive.default;

  return {
    ...createGlassSurfaceStyles({
      backgroundColor: elementTokens.background,
      borderRadius: resolvedBorderRadius,
      bottomShade: elementTokens.bottomOverlay,
      boxShadow: elementTokens.shadow,
      highlightEnd: 68,
      highlightStart: 0,
      overlayInset: 1,
      topHighlight: elementTokens.topOverlay
    }),
    ...createGlassBorderStyles({
      borderRadius: resolvedBorderRadius,
      gradientFrom: elementTokens.border.from,
      gradientMid: elementTokens.border.mid,
      gradientTo: elementTokens.border.to,
      opacity: 1
    }),
    position: "relative"
  };
};

export const getFloatingPanelStyles = (theme: Theme) => {
  const floatingPanel = theme.vars.ui.drawer;

  return {
    ...createGlassSurfaceStyles({
      backgroundColor: floatingPanel.background,
      borderRadius: theme.shape.borderRadiusMd,
      bottomShade: floatingPanel.bottomShade,
      boxShadow: floatingPanel.shadow,
      topHighlight: floatingPanel.topHighlight
    }),
    ...createGlassBorderStyles({
      borderRadius: theme.shape.borderRadiusMd,
      gradientAngle: 170,
      gradientFrom: floatingPanel.border.from,
      gradientMid: floatingPanel.border.mid,
      gradientTo: floatingPanel.border.to
    })
  };
};

/**
 * `highlightEnd` accepts a CSS length so a card can opt out of the default
 * height-relative ramp. Cards that must match each other visually despite
 * differing heights (the org dashboard section cards) pass a fixed length via
 * GlassCard's `highlightEnd` prop.
 */
export const getCardStyles = (theme: Theme, highlightEnd?: number | string) => {
  const card = theme.vars.ui.dashboardCard;

  return {
    ...createGlassSurfaceStyles({
      backgroundColor: card.background,
      borderRadius: theme.shape.borderRadiusMd,
      bottomShade: card.bottomOverlay,
      boxShadow: theme.vars.appEffects.shadow.card,
      highlightEnd,
      topHighlight: card.topOverlay
    }),
    ...createGlassBorderStyles({
      borderRadius: theme.shape.borderRadiusMd,
      gradientFrom: card.border.from,
      gradientMid: theme.vars.ui.glassInteractive.emphasized.border.mid,
      gradientTo: theme.vars.ui.glassInteractive.emphasized.border.to,
      opacity: 0.7
    })
  };
};

export const getSubCardStyles = (theme: Theme) => {
  const card = theme.vars.ui.dashboardCard;

  return {
    ...createGlassSurfaceStyles({
      backgroundColor: card.background,
      borderRadius: theme.shape.borderRadiusMd,
      bottomShade: card.bottomOverlay,
      boxShadow: theme.vars.appEffects.shadow.sm,
      gradientAngle: 360,
      highlightEnd: 70,
      topHighlight: theme.vars.palette.background.default
    }),
    border: `1px solid ${theme.vars.palette.border.subtle}`,
    position: "relative"
  };
};

export const getGlassElevationStyles = (theme: Theme): CSSObject => ({
  background: "rgba(255, 255, 255, 0.50)",
  boxShadow: "none",
  ...(theme.applyStyles("dark", {
    background: "rgba(25, 25, 26, 0.50)"
  }) as CSSObject)
});

export const getPdfFriendlyCardStyles = (
  theme: Theme,
  borderRadius?: number
): CSSObject => ({
  ...getGlassElevationStyles(theme),
  border: `1px solid ${theme.vars.ui.table.border}`,
  borderRadius: `${borderRadius ?? theme.shape.borderRadiusMd}px`
});

export const getGlassSurfaceStyles = (theme: Theme): CSSObject => {
  const elementTokens = theme.vars.ui.glassInteractive.emphasized;

  return {
    ...createGlassSurfaceStyles({
      backgroundColor: elementTokens.background,
      borderRadius: 999,
      bottomShade: elementTokens.bottomOverlay,
      boxShadow: elementTokens.shadow,
      highlightEnd: 68,
      highlightStart: 0,
      topHighlight: elementTokens.topOverlay
    }),
    position: "relative"
  };
};

/**
 * The inset surface a chart is drawn on — the second border inside an analytics
 * card. Lives here rather than on either chart component because both the Kendo
 * shell (`StyledKendoChart`) and the non-Kendo `ChartPanel` draw it, and a donut
 * card has to frame its contents exactly like a bar-chart card beside it.
 *
 * Whatever the panel frames belongs inside it — legends and chart scrollbars
 * included. That is what lines the bottom borders up across a row of cards.
 */
export const getChartPanelStyles = (theme: Theme): CSSObject => ({
  backgroundColor: theme.vars.palette.surface.overlay,
  border: `1px solid ${theme.vars.palette.border.subtle}`,
  borderRadius: theme.shape.borderRadiusMd,
  // Flat: a panel is separated from the page by its hairline, not by a lift.
  boxShadow: "none",
  overflow: "hidden",
  padding: "14px 16px"
});
